import * as React from 'react';
import { elementContains, mergeCallbacks, useControllableState, useEventCallback, useId, useMergedRefs } from '@fluentui/react-utilities';
import { Enter } from '@fluentui/keyboard-keys';
import { useCombobox_unstable, Option } from '@fluentui/react-combobox';
import { useFieldContext_unstable as useFieldContext } from '@fluentui/react-field';
import { dateToKey, keyToDate, formatDateToTimeString as defaultFormatDateToTimeString, getDateStartAnchor, getDateEndAnchor, getTimesBetween, getDateFromTimeString } from './timeMath';
/**
 * Create the state required to render TimePicker.
 *
 * The returned state can be modified with hooks such as useTimePickerStyles_unstable,
 * before being passed to renderTimePicker_unstable.
 *
 * @param props - props from this instance of TimePicker
 * @param ref - reference to root HTMLElement of TimePicker
 */ export const useTimePicker_unstable = (props, ref)=>{
    var _baseState_clearIcon;
    const { dateAnchor: dateAnchorInProps, defaultSelectedTime: defaultSelectedTimeInProps, endHour = 24, formatDateToTimeString = defaultFormatDateToTimeString, hourCycle, increment = 30, onTimeChange, selectedTime: selectedTimeInProps, showSeconds = false, startHour = 0, parseTimeStringToDate: parseTimeStringToDateInProps, ...rest } = props;
    const { freeform = false } = rest;
    var _ref;
    const { dateStartAnchor, dateEndAnchor } = useStableDateAnchor((_ref = dateAnchorInProps !== null && dateAnchorInProps !== void 0 ? dateAnchorInProps : selectedTimeInProps) !== null && _ref !== void 0 ? _ref : defaultSelectedTimeInProps, startHour, endHour);
    const options = React.useMemo(()=>getTimesBetween(dateStartAnchor, dateEndAnchor, increment).map((time)=>({
                date: time,
                key: dateToKey(time),
                text: formatDateToTimeString(time, {
                    showSeconds,
                    hourCycle
                })
            })), [
        dateEndAnchor,
        dateStartAnchor,
        formatDateToTimeString,
        hourCycle,
        increment,
        showSeconds
    ]);
    const [selectedTime, setSelectedTime] = useControllableState({
        state: selectedTimeInProps,
        defaultState: defaultSelectedTimeInProps,
        initialState: null
    });
    const [submittedText, setSubmittedText] = React.useState(undefined);
    const selectTime = useEventCallback((e, data)=>{
        setSelectedTime(data.selectedTime);
        setSubmittedText(data.selectedTimeText);
        onTimeChange === null || onTimeChange === void 0 ? void 0 : onTimeChange(e, data);
    });
    const selectedOptions = React.useMemo(()=>{
        const selectedTimeKey = dateToKey(selectedTime);
        const selectedOption = options.find((date)=>date.key === selectedTimeKey);
        return selectedOption ? [
            selectedOption.key
        ] : [];
    }, [
        options,
        selectedTime
    ]);
    const clearIconRef = React.useRef(null);
    const handleOptionSelect = useEventCallback((e, data)=>{
        if (freeform && data.optionValue === undefined && !(rest.clearable && e.type === 'click' && e.currentTarget === clearIconRef.current)) {
            // Combobox clears selection when input value not matching any option; but we allow this case in freeform TimePicker.
            return;
        }
        var _data_optionValue;
        const timeSelectionData = {
            selectedTime: keyToDate((_data_optionValue = data.optionValue) !== null && _data_optionValue !== void 0 ? _data_optionValue : ''),
            selectedTimeText: data.optionText,
            errorType: undefined
        };
        selectTime(e, timeSelectionData);
    });
    const baseState = useCombobox_unstable({
        autoComplete: 'off',
        ...rest,
        selectedOptions,
        onOptionSelect: handleOptionSelect,
        children: options.map((date)=>/*#__PURE__*/ React.createElement(Option, {
                key: date.key,
                value: date.key
            }, date.text))
    }, ref);
    const defaultParseTimeStringToDate = React.useCallback((time)=>getDateFromTimeString(time, dateStartAnchor, dateEndAnchor, {
            hourCycle,
            showSeconds
        }), [
        dateEndAnchor,
        dateStartAnchor,
        hourCycle,
        showSeconds
    ]);
    const mergedClearIconRef = useMergedRefs((_baseState_clearIcon = baseState.clearIcon) === null || _baseState_clearIcon === void 0 ? void 0 : _baseState_clearIcon.ref, clearIconRef);
    const state = {
        ...baseState,
        clearIcon: baseState.clearIcon ? {
            ...baseState.clearIcon,
            ref: mergedClearIconRef
        } : undefined,
        freeform,
        parseTimeStringToDate: parseTimeStringToDateInProps !== null && parseTimeStringToDateInProps !== void 0 ? parseTimeStringToDateInProps : defaultParseTimeStringToDate,
        submittedText
    };
    useDefaultChevronIconLabel(state);
    useSelectTimeFromValue(state, selectTime);
    return state;
};
/**
 * Provides stable start and end date anchors based on the provided date and time parameters.
 * The hook ensures that the memoization remains consistent even if new Date objects representing the same date are provided.
 */ const useStableDateAnchor = (providedDate, startHour, endHour)=>{
    const [fallbackDateAnchor] = React.useState(()=>new Date());
    const providedDateKey = dateToKey(providedDate !== null && providedDate !== void 0 ? providedDate : null);
    return React.useMemo(()=>{
        const dateAnchor = providedDate !== null && providedDate !== void 0 ? providedDate : fallbackDateAnchor;
        const dateStartAnchor = getDateStartAnchor(dateAnchor, startHour);
        const dateEndAnchor = getDateEndAnchor(dateAnchor, startHour, endHour);
        return {
            dateStartAnchor,
            dateEndAnchor
        };
    // `providedDate`'s stable key representation is used as dependency instead of the Date object. This ensures that the memoization remains stable when a new Date object representing the same date is passed in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        endHour,
        fallbackDateAnchor,
        providedDateKey,
        startHour
    ]);
};
/**
 * Mimics the behavior of the browser's change event for a freeform TimePicker.
 * The provided callback is called when input changed and:
 * - Enter/Tab key is pressed on the input.
 * - TimePicker loses focus, signifying a possible change.
 */ const useSelectTimeFromValue = (state, callback)=>{
    const { getOptionById, freeform, parseTimeStringToDate, submittedText, value, activeDescendantController } = state;
    const getActiveOption = React.useCallback(()=>{
        const activeOptionId = activeDescendantController.active();
        return activeOptionId ? getOptionById(activeOptionId) : null;
    }, [
        activeDescendantController,
        getOptionById
    ]);
    // Base Combobox has activeOption default to first option in dropdown even if it doesn't match input value, and Enter key will select it.
    // This effect ensures that the activeOption is cleared when the input doesn't match any option.
    // This behavior is specific to a freeform TimePicker where the input value is treated as a valid time even if it's not in the dropdown.
    React.useEffect(()=>{
        if (freeform && value) {
            const activeOption = getActiveOption();
            if (!activeOption) {
                return;
            }
            const valueMatchesActiveOption = activeOption.text.toLowerCase().indexOf(value.toLowerCase()) === 0;
            if (!valueMatchesActiveOption) {
                activeDescendantController.blur();
            }
        }
    }, [
        freeform,
        value,
        activeDescendantController,
        getActiveOption
    ]);
    const selectTimeFromValue = useEventCallback((e)=>{
        if (!freeform) {
            return;
        }
        const { date: selectedTime, errorType } = parseTimeStringToDate(value);
        // Only triggers callback when the text in input has changed.
        if (submittedText !== value) {
            callback === null || callback === void 0 ? void 0 : callback(e, {
                selectedTime,
                selectedTimeText: value,
                errorType
            });
        }
    });
    const handleKeyDown = useEventCallback((e)=>{
        const activeOption = getActiveOption();
        if (!activeOption && e.key === Enter) {
            selectTimeFromValue(e);
        }
    });
    state.root.onKeyDown = mergeCallbacks(handleKeyDown, state.root.onKeyDown);
    const rootRef = React.useRef(null);
    state.root.ref = useMergedRefs(state.root.ref, rootRef);
    if (state.listbox) {
        state.listbox.tabIndex = -1; // allows it to be the relatedTarget of a blur event.
    }
    if (state.expandIcon) {
        state.expandIcon.tabIndex = -1; // allows it to be the relatedTarget of a blur event.
    }
    const handleInputBlur = useEventCallback((e)=>{
        const isOutside = e.relatedTarget ? !elementContains(rootRef.current, e.relatedTarget) : true;
        if (isOutside) {
            selectTimeFromValue(e);
        }
    });
    state.input.onBlur = mergeCallbacks(handleInputBlur, state.input.onBlur);
};
/**
 * Provides a default aria-labelledby for the chevron icon if the TimePicker is wrapped in a Field.
 */ const useDefaultChevronIconLabel = (state)=>{
    var _state_expandIcon;
    const fieldContext = useFieldContext();
    const chevronDefaultId = useId('timepicker-chevron-');
    const defaultLabelFromCombobox = 'Open';
    if ((fieldContext === null || fieldContext === void 0 ? void 0 : fieldContext.labelId) && ((_state_expandIcon = state.expandIcon) === null || _state_expandIcon === void 0 ? void 0 : _state_expandIcon['aria-label']) === defaultLabelFromCombobox) {
        var _state_expandIcon_id;
        const chevronId = (_state_expandIcon_id = state.expandIcon.id) !== null && _state_expandIcon_id !== void 0 ? _state_expandIcon_id : chevronDefaultId;
        state.expandIcon['aria-labelledby'] = `${chevronId} ${fieldContext.labelId}`;
    }
};
